import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, FileText, XCircle } from 'lucide-react';
import { loadStripe, Stripe } from '@stripe/stripe-js';

import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import apiService from '../services/api';
import { useAuth } from '../hooks/useAuth';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const InvoicePaymentCompletePage: React.FC = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { logout } = useAuth();
  const query = useQuery();

  const paymentIntentId = query.get('payment_intent') || '';
  const paymentIntentClientSecret = query.get('payment_intent_client_secret') || '';
  const redirectStatus = query.get('redirect_status') || '';

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiService.getStripeConfig();
        setStripePromise(loadStripe(cfg.publishableKey));
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Stripe is not configured');
      }
    })();
  }, []);

  useEffect(() => {
    if (!invoiceId) return;
    if (!paymentIntentId) return;
    (async () => {
      try {
        setSyncing(true);
        const resp = await apiService.syncInvoicePaymentIntent(invoiceId, paymentIntentId);
        setStatus(resp.mappedStatus || resp.status);
      } catch (e: any) {
        // Non-fatal; we can still show Stripe-reported status via client secret.
        console.error('Payment sync failed', e);
      } finally {
        setSyncing(false);
      }
    })();
  }, [invoiceId, paymentIntentId]);

  useEffect(() => {
    if (!stripePromise) return;
    if (!paymentIntentClientSecret) return;
    (async () => {
      try {
        const stripe = await stripePromise;
        if (!stripe) return;
        const { paymentIntent } = await stripe.retrievePaymentIntent(paymentIntentClientSecret);
        if (paymentIntent?.status) setStatus(String(paymentIntent.status));
      } catch (e: any) {
        console.error('Failed to retrieve PaymentIntent', e);
      }
    })();
  }, [paymentIntentClientSecret, stripePromise]);

  const prettyStatus = (() => {
    const s = String(status || redirectStatus || '').toUpperCase();
    if (!s) return 'UNKNOWN';
    return s;
  })();

  const isSuccess = ['SUCCEEDED', 'SUCCEED', 'SUCCESS', 'PAID'].includes(prettyStatus) || prettyStatus === 'SUCCEEDED';
  const isFailed = ['FAILED', 'CANCELED', 'CANCELLED', 'REQUIRES_PAYMENT_METHOD'].includes(prettyStatus);

  if (!invoiceId) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={true} showLogoutButton={true} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Found</h1>
            <p className="text-gray-600 mb-6">Missing invoice ID.</p>
            <button onClick={() => navigate('/dashboard/customer/invoices')} className="aacore-btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Invoices
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      <CustomerNavbar onLogout={logout} showDashboardLink={true} showLogoutButton={true} />

      <div className="flex-1 pt-6 pb-12 overflow-visible">
        <div className="px-4 lg:px-8 max-w-2xl mx-auto overflow-visible">
          <div className="mb-4">
            <Link
              to={`/dashboard/customer/invoices/${encodeURIComponent(invoiceId)}`}
              className="flex items-center font-semibold"
              style={{ color: '#0B4778' }}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Invoice
            </Link>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center">
            {isSuccess ? (
              <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-3" />
            ) : isFailed ? (
              <XCircle className="w-14 h-14 text-red-600 mx-auto mb-3" />
            ) : (
              <FileText className="w-14 h-14 text-gray-500 mx-auto mb-3" />
            )}

            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isSuccess ? 'Payment successful' : isFailed ? 'Payment not completed' : 'Payment status'}
            </h1>
            <p className="text-gray-600 mb-0">
              Status: <span className="font-semibold text-gray-900">{prettyStatus}</span>
              {syncing ? ' (syncing…)': ''}
            </p>

            {error ? <p className="text-sm text-red-600 mt-3">{error}</p> : null}

            <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
              <Link to={`/dashboard/customer/invoices/${encodeURIComponent(invoiceId)}`} className="aacore-btn-primary justify-center inline-flex items-center gap-2">
                <FileText className="w-4 h-4" />
                View invoice
              </Link>
              <Link to="/dashboard/customer/invoices" className="aacore-btn-outlined justify-center inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to invoices
              </Link>
            </div>
          </div>

          <div className="mt-3 text-xs text-gray-500">
            If you don’t see the invoice marked paid yet, it may take a moment to update.
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InvoicePaymentCompletePage;

